import type Map from 'ol/Map';
import type { AppStore } from '../core/store';
import { createInitialState } from '../core/store';
import type { ProjectMetadata } from '../core/types';
import { createProjectArchive, loadProjectAtomically, sanitizeProjectName, suggestedProjectFilename, type ProjectViewState } from '../project/projectArchive';
import { ensureProjectPermission, isPickerCancellation, pickProjectToOpen, pickProjectToSave, supportsProjectOpenPicker, supportsProjectSavePicker, writeProjectBytes, type FileSystemAccessBrowser, type ProjectFileHandle } from '../project/fileSystemAccess';
import { loadWorkspaceLayoutPreference } from './workspaceLayout';

const required = <T extends Element = HTMLElement>(selector: string): T => { const element = document.querySelector<T>(selector); if (!element) throw new Error(`No se encontró ${selector}`); return element; };
const newMetadata = (name = 'Sin título'): ProjectMetadata => { const now = new Date().toISOString(); return { id: globalThis.crypto.randomUUID(), name, createdAt: now, updatedAt: now }; };

export class ProjectController {
  private busy = false;
  private fileHandle: ProjectFileHandle | null = null;

  constructor(private readonly store: AppStore, private readonly map: Map, private readonly afterProjectReplace: () => void | Promise<void> = () => {}) {
    const input = required<HTMLInputElement>('#project-file-input');
    required<HTMLInputElement>('#project-name').addEventListener('change', (event) => { const state = this.store.getState(); this.store.update({ project: { ...state.project, name: sanitizeProjectName((event.target as HTMLInputElement).value) } }); });
    required('#new-project').addEventListener('click', () => void this.newProject());
    required('#open-project').addEventListener('click', () => void this.requestOpen());
    required('#save-project').addEventListener('click', () => void this.save());
    required('#save-project-as').addEventListener('click', () => void this.saveAs());
    input.addEventListener('change', () => { const file = input.files?.[0]; input.value = ''; if (file) void this.open(file, null); });
    this.map.on('pointerdrag', () => this.store.markDirty()); this.map.getViewport().addEventListener('wheel', () => this.store.markDirty(), { passive: true });
    this.store.subscribe((state) => this.render(state.project, state.projectDirty)); this.render(this.store.getState().project, false);
  }

  private viewState(): ProjectViewState {
    const view = this.map.getView(); const center = view.getCenter() ?? [-9567000, 1285000];
    return { center: [center[0]!, center[1]!], zoom: view.getZoom() ?? 8, resolution: view.getResolution() ?? 0, rotation: view.getRotation() };
  }

  private render(project: ProjectMetadata, dirty: boolean): void {
    required<HTMLInputElement>('#project-name').value = project.name;
    required('#project-current').textContent = `Proyecto: ${project.name} · Archivo: ${this.fileHandle?.name ?? 'sin archivo asociado'}`;
    const status = required('#project-save-state'); status.textContent = dirty ? 'Cambios sin guardar' : 'Guardado'; status.setAttribute('data-dirty', String(dirty));
    required('#project-panel').classList.toggle('is-busy', this.busy);
  }

  private setStatus(text: string): void { required('#project-file-status').textContent = text; }
  private setBusy(busy: boolean): void { this.busy = busy; this.render(this.store.getState().project, this.store.getState().projectDirty); }

  async save(): Promise<boolean> {
    if (this.busy) return false;
    const browser = window as FileSystemAccessBrowser;
    if (!this.fileHandle && supportsProjectSavePicker(browser)) return this.saveAs();
    this.setBusy(true); this.setStatus('Validando y comprimiendo el proyecto…');
    try {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
      const archive = await createProjectArchive(this.store.getState(), this.viewState());
      if (this.fileHandle) {
        if (!await writeProjectBytes(this.fileHandle, archive.bytes)) {
          this.setStatus('No hay permiso de escritura para el archivo actual. Usa Guardar como… para elegir otro archivo.');
          return false;
        }
        this.store.markSaved(archive.manifest.updatedAt);
        this.setStatus(`Archivo actualizado · ${this.fileHandle.name} · ${archive.bytes.byteLength.toLocaleString('es-NI')} bytes.`);
        return true;
      }
      this.download(archive.bytes, suggestedProjectFilename(archive.manifest.projectName));
      this.store.markSaved(archive.manifest.updatedAt); this.setStatus(`Descarga local creada · ${archive.bytes.byteLength.toLocaleString('es-NI')} bytes comprimidos.`); return true;
    } catch (error) { this.setStatus(error instanceof Error ? error.message : 'No fue posible guardar el proyecto.'); return false; }
    finally { this.setBusy(false); }
  }

  async saveAs(): Promise<boolean> {
    if (this.busy) return false;
    const browser = window as FileSystemAccessBrowser;
    this.setBusy(true);
    try {
      let selectedHandle: ProjectFileHandle | null = null;
      if (supportsProjectSavePicker(browser)) {
        selectedHandle = await pickProjectToSave(browser, suggestedProjectFilename(this.store.getState().project.name));
        if (!selectedHandle) return false;
      }
      this.setStatus('Validando y comprimiendo el proyecto…');
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
      const archive = await createProjectArchive(this.store.getState(), this.viewState());
      if (selectedHandle) {
        if (!await writeProjectBytes(selectedHandle, archive.bytes)) {
          this.setStatus('No se concedió permiso para guardar en el archivo elegido.');
          return false;
        }
        this.fileHandle = selectedHandle;
        this.store.markSaved(archive.manifest.updatedAt);
        this.setStatus(`Guardado como · ${selectedHandle.name} · ${archive.bytes.byteLength.toLocaleString('es-NI')} bytes.`);
        return true;
      }
      this.download(archive.bytes, suggestedProjectFilename(archive.manifest.projectName));
      this.store.markSaved(archive.manifest.updatedAt);
      this.setStatus(`Descarga local creada · ${archive.bytes.byteLength.toLocaleString('es-NI')} bytes comprimidos.`);
      return true;
    } catch (error) {
      if (isPickerCancellation(error)) { this.setStatus('Guardado cancelado. El proyecto conserva sus cambios.'); return false; }
      this.setStatus(error instanceof Error ? error.message : 'No fue posible guardar el proyecto.'); return false;
    } finally { this.setBusy(false); }
  }

  private download(bytes: Uint8Array, filename: string): void {
    const blob = new Blob([bytes.buffer as ArrayBuffer], { type: 'application/zip' }); const url = URL.createObjectURL(blob); const anchor = document.createElement('a');
    anchor.href = url; anchor.download = filename; document.body.append(anchor); anchor.click(); anchor.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  private async confirmReplacement(): Promise<boolean> {
    if (!this.store.getState().projectDirty) return true; const dialog = required<HTMLDialogElement>('#unsaved-project-dialog');
    const action = await new Promise<'save' | 'discard' | 'cancel'>((resolve) => {
      const finish = (value: 'save' | 'discard' | 'cancel') => { dialog.removeEventListener('click', handler); dialog.removeEventListener('cancel', cancel); dialog.close(); resolve(value); };
      const handler = (event: Event) => { const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-unsaved-action]'); if (button) finish(button.dataset.unsavedAction as 'save' | 'discard' | 'cancel'); };
      const cancel = (event: Event) => { event.preventDefault(); finish('cancel'); };
      dialog.addEventListener('click', handler); dialog.addEventListener('cancel', cancel); dialog.showModal();
    });
    if (action === 'cancel') return false; return action === 'discard' || await this.save();
  }

  private async newProject(): Promise<void> {
    if (!await this.confirmReplacement()) return; this.fileHandle = null; this.store.replace(createInitialState(newMetadata(), loadWorkspaceLayoutPreference())); await this.afterProjectReplace(); const view = this.map.getView(); view.setCenter([-9567000, 1285000]); view.setZoom(8); view.setRotation(0); this.setStatus('Proyecto nuevo listo.');
  }

  private async requestOpen(): Promise<void> {
    if (!await this.confirmReplacement()) return;
    const browser = window as FileSystemAccessBrowser;
    if (!supportsProjectOpenPicker(browser)) { required<HTMLInputElement>('#project-file-input').click(); return; }
    try {
      const handle = await pickProjectToOpen(browser);
      if (!handle) return;
      if (!await ensureProjectPermission(handle, 'read')) { this.setStatus('No se concedió permiso para leer el archivo seleccionado.'); return; }
      const file = await handle.getFile();
      await this.open(file, handle);
    } catch (error) {
      if (isPickerCancellation(error)) { this.setStatus('Apertura cancelada.'); return; }
      this.setStatus(error instanceof Error ? error.message : 'No fue posible abrir el archivo seleccionado.');
    }
  }

  private async open(file: File, handle: ProjectFileHandle | null): Promise<void> {
    if (this.busy) return; if (!/\.casurgeo$/i.test(file.name)) { this.setStatus('Selecciona un archivo .casurgeo.'); return; }
    this.setBusy(true); this.setStatus('Validando integridad y reconstruyendo el proyecto…');
    try {
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
      const restored = await loadProjectAtomically(this.store, file, loadWorkspaceLayoutPreference()); await this.afterProjectReplace(); const view = this.map.getView(); view.setCenter([...restored.view.center]);
      if (Number.isFinite(restored.view.resolution) && restored.view.resolution > 0) view.setResolution(restored.view.resolution); else view.setZoom(restored.view.zoom);
      view.setRotation(restored.view.rotation); this.fileHandle = handle; this.render(this.store.getState().project, this.store.getState().projectDirty); this.setStatus(`Proyecto abierto · ${handle?.name ?? file.name} · formato v${restored.manifest.formatVersion} · ${restored.manifest.datasets.length} dataset(s).`);
    } catch (error) { this.setStatus(error instanceof Error ? error.message : 'No fue posible abrir el proyecto.'); }
    finally { this.setBusy(false); }
  }
}
